// UsernameModal Component
//
// Two ways through this:
//   quick save  — pirate name, one tap, done. The account still gets a real
//                 password, we just generate it instead of asking; it lands in
//                 localStorage via authService like any other login, so the
//                 player stays signed in on this device and can change avatars.
//   full signup — name → password → avatar, for anyone who wants to log in
//                 somewhere else. Same three steps as before.
//
// Why: of 30 people who finished a quiz in the 28 days to 2026-07-25, only 5
// saved a score. The old flow demanded a password and an avatar from someone who
// just wanted their name on a board.

import React, { useState, useEffect, useRef } from 'react';
import { validateUsername, validatePassword } from '../../hooks/useUsername';
import { getScoreStanding, formatTime } from '../../services/leaderboardService';
import { checkUsernameExists } from '../../services/authService';
import { DEFAULT_AVATAR } from '../../config/avatarConfig';
import Avatar from '../Avatar/Avatar';
import PasswordField from '../PasswordField/PasswordField';
import AvatarPicker from '../AvatarPicker/AvatarPicker';
import './UsernameModal.css';

// A password the player never has to see or remember. Same strength as anything
// they'd have typed; authService stores it for auto-login exactly like a real one.
const generatePassword = () => {
  const bytes = new Uint8Array(18);
  const crypto = window.crypto || window.msCrypto;
  if (crypto && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // Old browsers without crypto.getRandomValues. Weaker, but nobody should
    // lose a finished game over it — this password is never typed by anyone.
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('');
};

// allowQuickSave is off on /login, where the whole point is credentials the
// player chose and can type again on another device.
// `standing` (optional) is the run that just finished: { gameType, score,
// totalTime, max }. With it the modal opens by telling the player where that
// run would put them, which is the reason to type a name at all.
const UsernameModal = ({ isOpen, onSubmit, onSkip, initialUsername = '', allowQuickSave = true, standing = null }) => {
  // Form state
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(DEFAULT_AVATAR);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Multi-step flow: 1 = username, 2 = password, 3 = avatar
  const [step, setStep] = useState(1);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);

  // Where this run would rank. Decorative: if the board cannot be reached the
  // modal looks exactly as it did before.
  const [placing, setPlacing] = useState(null);

  useEffect(() => {
    if (!isOpen || !standing || typeof standing.score !== 'number') return undefined;
    let live = true;
    getScoreStanding(standing.gameType, standing.score, standing.totalTime)
      .then((result) => { if (live && result) setPlacing(result); })
      .catch(() => {});
    return () => { live = false; };
  }, [isOpen, standing]);

  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Focus appropriate input when modal opens or step changes
  useEffect(() => {
    if (isOpen) {
      if (step === 1 && usernameInputRef.current) {
        setTimeout(() => usernameInputRef.current?.focus(), 100);
      } else if (step === 2 && passwordInputRef.current) {
        setTimeout(() => passwordInputRef.current?.focus(), 100);
      }
    }
  }, [isOpen, step]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUsername(initialUsername);
      setPassword('');
      setConfirmPassword('');
      setEmail('');
      setSelectedAvatar(DEFAULT_AVATAR);
      setError('');
      setIsSubmitting(false);
      setStep(1);
      setIsCheckingUsername(false);
    }
  }, [isOpen, initialUsername]);

  // Handle username input
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) setError('');
  };

  // Handle password input
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');
  };

  // Handle confirm password input
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (error) setError('');
  };

  // Validate the name and make sure it's free. Returns true if we can proceed.
  const claimUsername = async () => {
    const validation = validateUsername(username);
    if (!validation.valid) {
      setError(validation.error);
      return false;
    }

    setIsCheckingUsername(true);
    setError('');

    try {
      if (await checkUsernameExists(username)) {
        setError('Username already taken. Try another!');
        setIsCheckingUsername(false);
        return false;
      }
    } catch (err) {
      setError('Error checking username. Please try again.');
      setIsCheckingUsername(false);
      return false;
    }

    setIsCheckingUsername(false);
    return true;
  };

  // Quick save: name, then straight onto the leaderboard.
  const handleQuickSave = async (e) => {
    e.preventDefault();
    if (!(await claimUsername())) return;

    setIsSubmitting(true);
    try {
      await onSubmit(username.trim(), generatePassword(), DEFAULT_AVATAR, '');
    } catch (err) {
      setError('Failed to save your score. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Full signup: name is free, go collect a password.
  const handleCheckUsername = async (e) => {
    e.preventDefault();
    if (await claimUsername()) setStep(2);
  };

  // Step 2: Validate password and move to avatar
  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Move to avatar selection step
    setStep(3);
  };

  // Step 3: Submit registration with avatar
  const handleSubmitRegistration = async () => {
    setIsSubmitting(true);

    try {
      // Call parent's onSubmit with username, password, avatar, and optional email
      await onSubmit(username.trim(), password, selectedAvatar, email.trim());
    } catch (err) {
      setError('Failed to create account. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Go back to previous step
  const handleBackToUsername = () => {
    setStep(1);
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleBackToPassword = () => {
    setStep(2);
    setError('');
  };

  // Handle skip
  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  // Handle escape key
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleSkip();
    }
  };

  if (!isOpen) return null;

  return (
    // No click-outside-to-close: this modal is the only thing standing between a
    // finished game and a saved score, and a stray tap used to throw it away.
    // Skip is still one explicit button (or Escape) away.
    <div className="username-modal-overlay" onKeyDown={handleKeyDown}>
      <div className="username-modal" onClick={(e) => e.stopPropagation()}>
        {/* Step 1: Username */}
        {step === 1 && (
          <>
            <div className="modal-header">
              <div className="modal-icon">🏴‍☠️</div>
              <h2 className="modal-title">
                {allowQuickSave ? 'Save Your Score!' : 'Enter Your Pirate Name!'}
              </h2>
              <p className="modal-subtitle">
                {allowQuickSave
                  ? 'Pick a pirate name and you\'re on the leaderboard'
                  : 'Choose a unique name for the leaderboard'}
              </p>
            </div>

            {standing && typeof standing.score === 'number' && (
              <div className="run-standing">
                <p className="run-standing-score">
                  You scored <b>{standing.score}</b> out of {standing.max || 15}
                  {typeof standing.totalTime === 'number' && (
                    <span className="run-standing-time"> in {standing.totalTime} seconds</span>
                  )}
                </p>
                {placing && (
                  <>
                    {/* A rank is worth showing while it is something to be pleased
                        about. Near the bottom it only reads as "you are last", so
                        that half of the board is told what to aim at instead. */}
                    {placing.rank <= Math.ceil(placing.players / 2) ? (
                      <>
                        <p className="run-standing-rank">
                          {placing.rank === 1
                            ? 'Nobody on the board has beaten that'
                            : `You would be number ${placing.rank} out of ${placing.players} players`}
                        </p>
                        {placing.best && placing.rank > 1 && (
                          <p className="run-standing-hint">
                            {placing.best.username} is first with {placing.best.score} out of {standing.max || 15}
                          </p>
                        )}
                      </>
                    ) : (
                      placing.topTen && (
                        <>
                          <p className="run-standing-rank">
                            {placing.topTen.score >= (standing.max || 15)
                              ? `Everyone in the top 10 scored ${placing.topTen.score} out of ${standing.max || 15}`
                              : `You need ${placing.topTen.score} out of ${standing.max || 15} to reach the top 10`}
                          </p>
                          <p className="run-standing-hint">
                            {placing.topTen.score >= (standing.max || 15)
                              ? 'Get them all right and the fastest time wins'
                              : 'Save this one and try again'}
                          </p>
                        </>
                      )
                    )}
                  </>
                )}
              </div>
            )}

            <form onSubmit={allowQuickSave ? handleQuickSave : handleCheckUsername} className="modal-form">
              <div className="input-container">
                <input
                  ref={usernameInputRef}
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="e.g., Luffy_Fan_99"
                  className={`username-input ${error ? 'has-error' : ''}`}
                  maxLength={15}
                  disabled={isCheckingUsername}
                  autoComplete="off"
                  spellCheck="false"
                />
                <span className="char-count">{username.length}/15</span>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="rules-hint">
                <span>3-15 characters</span>
                <span>•</span>
                <span>Letters, numbers, underscores</span>
              </div>

              <button
                type="submit"
                className={`submit-button ${isCheckingUsername || isSubmitting ? 'submitting' : ''}`}
                disabled={isCheckingUsername || isSubmitting || username.trim().length < 3}
              >
                {isCheckingUsername || isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    {isSubmitting ? 'Saving...' : 'Checking...'}
                  </>
                ) : (
                  <>
                    <span className="btn-icon">{allowQuickSave ? '🏆' : '→'}</span>
                    {allowQuickSave ? 'Save My Score' : 'Continue'}
                  </>
                )}
              </button>
            </form>

            {allowQuickSave && (
              <button
                className="secondary-link-button"
                onClick={handleCheckUsername}
                disabled={isCheckingUsername || isSubmitting || username.trim().length < 3}
              >
                Set a password &amp; pick an avatar instead
              </button>
            )}

            <button
              className="skip-button"
              onClick={handleSkip}
              disabled={isCheckingUsername || isSubmitting}
            >
              Skip (score won't be saved)
            </button>
          </>
        )}

        {/* Step 2: Password */}
        {step === 2 && (
          <>
            <div className="modal-header">
              <div className="modal-icon">🔐</div>
              <h2 className="modal-title">Create Password</h2>
              <p className="modal-subtitle">
                Secure your account: <strong>{username}</strong>
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="modal-form">
              <div className="input-container">
                <PasswordField
                  ref={passwordInputRef}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password (min 6 characters)"
                  className={`username-input ${error ? 'has-error' : ''}`}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </div>

              <div className="input-container">
                <PasswordField
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm password"
                  className={`username-input ${error ? 'has-error' : ''}`}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </div>

              <div className="input-container">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  className="username-input"
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="rules-hint">
                <span>Minimum 6 characters</span>
              </div>

              <button
                type="submit"
                className={`submit-button`}
                disabled={password.length < 6 || confirmPassword.length < 6}
              >
                <span className="btn-icon">→</span>
                Continue
              </button>
            </form>

            <button className="skip-button back-button" onClick={handleBackToUsername}>
              ← Back to username
            </button>
          </>
        )}

        {/* Step 3: Avatar Selection */}
        {step === 3 && (
          <>
            <div className="modal-header">
              <div className="modal-icon"><Avatar value={selectedAvatar} alt="" /></div>
              <h2 className="modal-title">Choose Your Avatar!</h2>
              <p className="modal-subtitle">
                Pick an icon for <strong>{username}</strong>
              </p>
            </div>

            <AvatarPicker value={selectedAvatar} onChange={setSelectedAvatar} disabled={isSubmitting} />

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <button
              type="button"
              className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
              onClick={handleSubmitRegistration}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Creating Account...
                </>
              ) : (
                <>
                  <span className="btn-icon">⚔️</span>
                  {allowQuickSave ? 'Create Account & Save Score' : 'Create Account'}
                </>
              )}
            </button>

            <button className="skip-button back-button" onClick={handleBackToPassword} disabled={isSubmitting}>
              ← Back to password
            </button>
          </>
        )}

        {/* Step indicator — only once there are actually steps to track. Showing
            "1 of 3" on the quick-save screen makes a one-tap save look like a
            signup form, which is the thing we're getting rid of. */}
        {(!allowQuickSave || step > 1) && (
          <div className="step-indicator">
            <div className={`step-dot ${step >= 1 ? 'active' : ''}`}></div>
            <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
            <div className={`step-dot ${step >= 3 ? 'active' : ''}`}></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsernameModal;
